const YCS_SELECTOR = '[data-ycs-root]';

class YhoneCreatorStories extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.dialog = this.querySelector('[data-ycs-dialog]');
    this.canvas = this.querySelector('[data-ycs-canvas]');
    this.frame = this.querySelector('[data-ycs-frame]');
    this.mediaShell = this.querySelector('[data-ycs-media-shell]');
    this.progress = this.querySelector('[data-ycs-progress]');
    this.counter = this.querySelector('[data-ycs-counter]');
    this.soundButton = this.querySelector('[data-ycs-sound]');
    this.closeButton = this.querySelector('[data-ycs-close]');
    this.previousButton = this.querySelector('[data-ycs-previous]');
    this.nextButton = this.querySelector('[data-ycs-next]');
    this.playButton = this.querySelector('[data-ycs-play]');
    this.triggers = Array.from(this.querySelectorAll('[data-ycs-open]'));
    this.templates = Array.from(this.querySelectorAll('template[data-ycs-template]'));

    if (!this.dialog || !this.canvas || !this.templates.length) return;

    this.initialized = true;
    this.controller = new AbortController();
    this.signal = this.controller.signal;
    this.imageDuration = Number.parseInt(this.dataset.ycsImageDuration, 10) || 5000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.activeIndex = 0;
    this.activeMedia = null;
    this.activeType = '';
    this.currentProgress = 0;
    this.imageAnimationFrame = 0;
    this.soundEnabled = false;
    this.pointerStart = null;
    this.lastTrigger = null;

    this.buildProgress();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.cleanupActiveMedia();
    if (this.dialog?.open) this.dialog.close();
    this.initialized = false;
  }

  bindEvents() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const index = Number.parseInt(trigger.dataset.ycsOpen, 10) || 0;
        this.open(index, trigger);
      }, { signal: this.signal });
    });

    this.closeButton?.addEventListener('click', () => this.close(), { signal: this.signal });
    this.previousButton?.addEventListener('click', () => this.previous(), { signal: this.signal });
    this.nextButton?.addEventListener('click', () => this.next(), { signal: this.signal });
    this.soundButton?.addEventListener('click', () => this.toggleSound(), { signal: this.signal });
    this.playButton?.addEventListener('click', () => this.togglePlayback(), { signal: this.signal });

    this.canvas?.addEventListener('click', (event) => {
      if (event.target.closest('button')) return;
      if (this.activeType === 'video') this.togglePlayback();
    }, { signal: this.signal });

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    }, { signal: this.signal });

    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    }, { signal: this.signal });

    this.dialog.addEventListener('close', () => this.handleClosed(), { signal: this.signal });
    this.dialog.addEventListener('keydown', (event) => this.handleKeydown(event), { signal: this.signal });

    this.mediaShell?.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }, { signal: this.signal });

    this.mediaShell?.addEventListener('pointerup', (event) => this.handleSwipe(event), { signal: this.signal });
    this.mediaShell?.addEventListener('pointercancel', () => {
      this.pointerStart = null;
    }, { signal: this.signal });

    document.addEventListener('visibilitychange', () => this.handleVisibilityChange(), { signal: this.signal });
  }

  buildProgress() {
    this.progress.replaceChildren();
    this.progress.style.setProperty('--ycs-progress-count', this.templates.length);

    this.progressSegments = this.templates.map(() => {
      const segment = document.createElement('span');
      segment.className = 'ycs__progress-segment';
      segment.style.setProperty('--ycs-segment-progress', 0);
      this.progress.append(segment);
      return segment;
    });
  }

  open(index, trigger) {
    this.lastTrigger = trigger;
    this.soundEnabled = false;

    this.triggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
    trigger?.setAttribute('aria-expanded', 'true');

    if (!this.dialog.open) {
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
    }

    this.render(index);
    window.requestAnimationFrame(() => this.closeButton?.focus({ preventScroll: true }));
  }

  close() {
    if (!this.dialog.open) return;
    this.dialog.close();
  }

  handleClosed() {
    this.cleanupActiveMedia();
    this.canvas.replaceChildren();
    this.triggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
    this.lastTrigger?.focus({ preventScroll: true });
    this.lastTrigger = null;
  }

  render(index) {
    if (!this.templates.length) return;

    this.cleanupActiveMedia();
    this.activeIndex = Math.max(0, Math.min(index, this.templates.length - 1));

    const template = this.templates[this.activeIndex];
    const media = template.content.firstElementChild?.cloneNode(true);
    if (!media) return;

    const ratioValue = Number.parseFloat(template.dataset.ycsRatio);
    const safeRatio = Number.isFinite(ratioValue) ? Math.min(2, Math.max(0.4, ratioValue)) : 0.5625;

    this.activeType = template.dataset.ycsType || '';
    this.activeMedia = media;
    this.currentProgress = 0;
    this.frame.style.setProperty('--ycs-viewer-ratio', safeRatio);
    this.canvas.replaceChildren(media);
    this.counter.textContent = `${this.activeIndex + 1} / ${this.templates.length}`;
    this.previousButton.disabled = this.activeIndex === 0;
    this.nextButton.disabled = this.activeIndex === this.templates.length - 1;
    this.updateProgress(0);

    if (this.activeType === 'image') {
      this.prepareImage(media);
    } else if (this.activeType === 'video') {
      this.prepareVideo(media);
    }
  }

  prepareImage(image) {
    this.soundButton.hidden = true;
    this.playButton.hidden = true;

    let started = false;
    const begin = () => {
      if (started) return;
      started = true;
      this.startImageProgress();
    };
    image.addEventListener('load', begin, { once: true });
    image.addEventListener('error', begin, { once: true });

    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    if (image.dataset.sizes) image.sizes = image.dataset.sizes;
    image.src = image.dataset.src || '';

    if (image.complete) begin();
  }

  prepareVideo(video) {
    this.mediaController = new AbortController();
    const mediaSignal = this.mediaController.signal;
    this.soundButton.hidden = false;
    this.playButton.hidden = true;
    video.muted = !this.soundEnabled;

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
    });

    video.addEventListener('timeupdate', () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      this.updateProgress(video.currentTime / video.duration);
    }, { signal: mediaSignal });

    video.addEventListener('ended', () => this.nextOrClose(), { signal: mediaSignal });
    video.addEventListener('play', () => {
      this.playButton.hidden = true;
      this.playButton.setAttribute('aria-label', 'Pause video');
    }, { signal: mediaSignal });
    video.addEventListener('pause', () => {
      if (video.ended) return;
      this.playButton.hidden = false;
      this.playButton.setAttribute('aria-label', 'Play video');
    }, { signal: mediaSignal });

    this.updateSoundButton();
    video.load();

    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        this.playButton.hidden = false;
        this.playButton.setAttribute('aria-label', 'Play video');
      });
    }
  }

  startImageProgress(initialProgress = 0) {
    this.cancelImageProgress();

    if (this.reducedMotion.matches) {
      this.updateProgress(initialProgress);
      return;
    }

    const duration = this.imageDuration;
    const startedAt = performance.now() - initialProgress * duration;

    const tick = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      this.updateProgress(progress);

      if (progress >= 1) {
        this.nextOrClose();
        return;
      }

      this.imageAnimationFrame = window.requestAnimationFrame(tick);
    };

    this.imageAnimationFrame = window.requestAnimationFrame(tick);
  }

  cancelImageProgress() {
    if (!this.imageAnimationFrame) return;
    window.cancelAnimationFrame(this.imageAnimationFrame);
    this.imageAnimationFrame = 0;
  }

  updateProgress(value) {
    this.currentProgress = Math.max(0, Math.min(1, Number(value) || 0));

    this.progressSegments.forEach((segment, index) => {
      let progress = 0;
      if (index < this.activeIndex) progress = 1;
      if (index === this.activeIndex) progress = this.currentProgress;
      segment.style.setProperty('--ycs-segment-progress', progress);
    });
  }

  cleanupActiveMedia() {
    this.cancelImageProgress();
    this.mediaController?.abort();
    this.mediaController = null;

    if (this.activeMedia instanceof HTMLVideoElement) {
      this.activeMedia.pause();
      this.activeMedia.removeAttribute('src');
      this.activeMedia.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      this.activeMedia.load();
    }

    this.activeMedia = null;
    this.activeType = '';
  }

  previous() {
    if (this.activeIndex <= 0) return;
    this.render(this.activeIndex - 1);
  }

  next() {
    if (this.activeIndex >= this.templates.length - 1) return;
    this.render(this.activeIndex + 1);
  }

  nextOrClose() {
    if (this.activeIndex < this.templates.length - 1) {
      this.render(this.activeIndex + 1);
    } else {
      this.close();
    }
  }

  toggleSound() {
    if (!(this.activeMedia instanceof HTMLVideoElement)) return;

    this.soundEnabled = !this.soundEnabled;
    this.activeMedia.muted = !this.soundEnabled;
    this.updateSoundButton();

    if (this.activeMedia.paused) {
      this.activeMedia.play().catch(() => {
        this.playButton.hidden = false;
      });
    }
  }

  updateSoundButton() {
    const isOn = this.soundEnabled;
    this.soundButton.setAttribute('aria-pressed', String(isOn));
    this.soundButton.setAttribute('aria-label', isOn ? 'Mute sound' : 'Turn sound on');
  }

  togglePlayback() {
    if (!(this.activeMedia instanceof HTMLVideoElement)) return;

    if (this.activeMedia.paused) {
      this.activeMedia.play().catch(() => {
        this.playButton.hidden = false;
      });
    } else {
      this.activeMedia.pause();
    }
  }

  handleKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === ' ' && this.activeType === 'video') {
      event.preventDefault();
      this.togglePlayback();
    }
  }

  handleSwipe(event) {
    if (!this.pointerStart) return;

    const deltaX = event.clientX - this.pointerStart.x;
    const deltaY = event.clientY - this.pointerStart.y;
    this.pointerStart = null;

    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
    if (deltaX > 0) this.previous();
    if (deltaX < 0) this.next();
  }

  handleVisibilityChange() {
    if (!this.dialog.open) return;

    if (document.hidden) {
      if (this.activeMedia instanceof HTMLVideoElement) this.activeMedia.pause();
      this.cancelImageProgress();
      return;
    }

    if (this.activeType === 'image') this.startImageProgress(this.currentProgress);
  }
}

if (!customElements.get('yhone-creator-stories')) {
  customElements.define('yhone-creator-stories', YhoneCreatorStories);
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll(YCS_SELECTOR).forEach((element) => {
    if (element instanceof YhoneCreatorStories) element.connectedCallback();
  });
});
